import { useState, useEffect } from "react"
import { format } from "date-fns"
import { fr } from "date-fns/locale"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useAllSchoolPeriods } from "@/hooks/findAllSchoolPeriods"
import { SchoolPeriod } from "@/entities/schoolYear/SchoolPeriod"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { useTimeMachine } from "@/contexts/TimeMachineContext"

interface Props {
   setSelectedSchoolPeriod: (period: SchoolPeriod) => void
}

export function SchoolPeriodSelector({ setSelectedSchoolPeriod }: Props) {
   const { now } = useTimeMachine()
   const { data: schoolYear, ...query } = useCurrentSchoolYear()
   const periods = useAllSchoolPeriods(schoolYear)

   const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
   const currentPeriod = periods?.[currentPeriodIndex]

   useEffect(() => {
      if (periods && periods.length > 0) {
         const initialPeriodIndex = (() => {
            if (!periods?.length) return 0
            const index = periods.findIndex((period, i) => {
               const nextPeriod = periods[i + 1]
               if (!nextPeriod) return true // Last period
               return period.start_date <= now && now < nextPeriod.start_date
            })
            return index === -1 ? 0 : index
         })()

         setCurrentPeriodIndex(initialPeriodIndex)
      }
   }, [periods, format(now, "yyyy-MM-dd")])

   useEffect(() => {
      if (!currentPeriod) return
      setSelectedSchoolPeriod(currentPeriod)
   }, [currentPeriod])

   return (
      <QueryRender query={query} data={currentPeriod}>
         {(currentPeriod) => {
            return (
               <div className="filter-bar justify-between">
                  <button
                     className="btn btn-circle btn-ghost"
                     onClick={() => setCurrentPeriodIndex((prev) => Math.max(0, prev - 1))}
                     disabled={currentPeriodIndex === 0}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M15 19l-7-7 7-7"
                        />
                     </svg>
                  </button>
                  <div className="flex flex-col sm:flex-row items-center gap-1">
                     <div className="text-lg font-medium">
                        {format(currentPeriod.start_date, "d MMMM  ", { locale: fr })}
                     </div>
                     <div className="text-base text-base-content/70 flex items-center gap-2">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           strokeWidth={1.5}
                           stroke="currentColor"
                           className="w-4 h-4"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                           />
                        </svg>
                        {format(currentPeriod.end_date, "d MMMM  ", { locale: fr })}
                     </div>
                  </div>
                  <button
                     className="btn btn-circle btn-ghost"
                     onClick={() =>
                        setCurrentPeriodIndex((prev) => Math.min(periods.length - 1, prev + 1))
                     }
                     disabled={currentPeriodIndex === periods.length - 1}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M9 5l7 7-7 7"
                        />
                     </svg>
                  </button>
               </div>
            )
         }}
      </QueryRender>
   )
}
